import { useFocusRing } from '@react-aria/focus';
import { getInteractionModality, useHover, usePress } from '@react-aria/interactions';
import { focusWithoutScrolling, getScrollParent, mergeProps, scrollIntoViewport } from '@react-aria/utils';
import clsx from 'clsx';
import { type DOMAttributes, useEffect, useRef } from 'react';
import type { NativeElementProps } from '../../types.js';
import { textStyle } from '../../typography/utils.js';
import type { ViewGridCellData, ViewGridState } from './types.js';

/** @internal */
export function ViewGridCell({ cellData, state }: { cellData: ViewGridCellData | null; state: ViewGridState }) {
  const ref = useRef<HTMLDivElement>(null);

  const isDisabled = cellData ? state.isDisabled || state.isCellDisabled(cellData.date) : true;
  const isSelected = cellData ? !isDisabled && state.isCellSelected(cellData.date) : false;
  const isTabbable = cellData ? state.isCellTabbable(cellData.date) : false;
  const isFocused = state.isFocused && isTabbable;

  const { pressProps, isPressed } = usePress({
    preventFocusOnPress: true,
    isDisabled: isDisabled || state.isReadOnly,
    onPress: () => {
      if (!state.isReadOnly && cellData) {
        state.setFocusedDate(cellData.date);
        state.selectFocusedDate();
      }
    },
  });

  let tabIndex: number | undefined = undefined;
  if (!isDisabled) {
    tabIndex = isTabbable ? 0 : -1;
  }

  // Focus the button in the DOM when the state updates.
  useEffect(() => {
    if (isFocused && ref.current) {
      focusWithoutScrolling(ref.current);

      // Scroll into view if navigating with a keyboard
      if (getInteractionModality() !== 'pointer' && document.activeElement === ref.current) {
        scrollIntoViewport(ref.current, { containingElement: getScrollParent(ref.current) });
      }
    }
  }, [isFocused]);

  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { focusProps, isFocusVisible } = useFocusRing();

  const cellProps: NativeElementProps<HTMLTableCellElement> = {
    role: 'gridcell',
    'aria-disabled': isDisabled || undefined,
    'aria-selected': isSelected || undefined,
  };

  if (!cellData) {
    return <td {...cellProps} />;
  }

  const { date, content, ariaLabel, isCurrent } = cellData;

  const buttonProps = mergeProps(pressProps, hoverProps, focusProps, {
    onFocus() {
      if (!isDisabled) {
        state.setFocusedDate(date);
      }
    },
    tabIndex,
    role: 'button',
    'aria-disabled': isDisabled || undefined,
    'aria-label': ariaLabel,
    onContextMenu(e) {
      e.preventDefault();
    },
  } as DOMAttributes<HTMLDivElement>);

  return (
    <td {...cellProps}>
      <div
        {...buttonProps}
        ref={ref}
        className={clsx(
          'cim-date-picker-calendar-cell',
          textStyle({ variant: isCurrent ? 'medium-semibold' : 'medium' }),
        )}
        data-disabled={isDisabled || undefined}
        data-hovered={isHovered || undefined}
        data-pressed={isPressed || undefined}
        data-selected={isSelected || undefined}
        data-focus-visible={(isFocusVisible && isFocused) || undefined}
        data-current={isCurrent || undefined}
      >
        {content}
      </div>
    </td>
  );
}
